/**
 * Discord OAuth Callback Handler
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/shared/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { apiUrl } from "@/lib/api";

export const DiscordCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const handledRef = useRef(false);
    const [status, setStatus] = useState<"loading" | "success" | "error">(
        "loading",
    );
    const [message, setMessage] = useState("Autenticazione in corso...");

    useEffect(() => {
        if (handledRef.current) return;
        handledRef.current = true;

        const persistAuthAndRedirect = (data: any) => {
            if (!data?.access_token) return false;

            localStorage.setItem("auth_token", data.access_token);

            if (data.user_id && data.username) {
                localStorage.setItem(
                    "auth_user",
                    JSON.stringify({
                        id: data.user_id,
                        username: data.username,
                        discord_id: data.discord_id,
                        role: data.role,
                    }),
                );
            }

            if (data.refresh_token) {
                localStorage.setItem("refresh_token", data.refresh_token);
            }

            setStatus("success");
            setMessage("Login riuscito! Reindirizzamento...");
            const redirectTarget =
                typeof data.redirect_url === "string" &&
                data.redirect_url.startsWith("/admin")
                    ? data.redirect_url
                    : "/admin/dashboard";
            setTimeout(() => {
                window.location.replace(redirectTarget);
            }, 1000);
            return true;
        };

        const handleCallback = async () => {
            const hashParams = new URLSearchParams(
                window.location.hash.replace(/^#/, ""),
            );
            const hashToken = hashParams.get("access_token");
            if (hashToken) {
                const handled = persistAuthAndRedirect({
                    access_token: hashToken,
                    refresh_token: hashParams.get("refresh_token"),
                    user_id: hashParams.get("user_id"),
                    username: hashParams.get("username"),
                    discord_id: hashParams.get("discord_id"),
                    role: hashParams.get("role"),
                });
                if (handled) return;
            }

            const code = searchParams.get("code");
            const state = searchParams.get("state");
            const error = searchParams.get("error");

            console.log("🔐 Discord Callback:", {
                code: code?.substring(0, 10),
                state,
                error,
            });

            if (error) {
                setStatus("error");
                setMessage(`Errore Discord: ${error}`);
                setTimeout(() => navigate("/login"), 3000);
                return;
            }

            if (!code || !state) {
                setStatus("error");
                setMessage("Parametri mancanti nella risposta Discord");
                setTimeout(() => navigate("/login"), 3000);
                return;
            }

            try {
                const frontendRedirectUri = `${window.location.origin}/auth/discord/callback`;
                const callbackUrl = apiUrl(
                    `/api/v1/auth/discord/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&redirect_uri=${encodeURIComponent(frontendRedirectUri)}`,
                );
                console.log("📡 Calling backend callback:", callbackUrl);

                const response = await fetch(callbackUrl);

                // Parse con gestione errori
                const text = await response.text();
                console.log("📡 Response text:", text.substring(0, 200));

                let data;
                try {
                    data = JSON.parse(text);
                } catch {
                    console.error("❌ JSON parse error:", text);
                    throw new Error("Server returned invalid JSON");
                }

                console.log("📡 Backend response:", response.status, data);

                if (response.ok && data.access_token) {
                    persistAuthAndRedirect(data);
                } else {
                    setStatus("error");
                    setMessage(data.detail || "Errore durante il login");
                    setTimeout(() => navigate("/login"), 3000);
                }
            } catch (error) {
                console.error("❌ Callback error:", error);
                setStatus("error");
                setMessage("Errore di connessione al server");
                setTimeout(() => navigate("/login"), 3000);
            }
        };

        handleCallback();
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-primary/10 to-background flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
            >
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl">
                            {status === "loading" &&
                                "🔄 Autenticazione Discord"}
                            {status === "success" && "✅ Login Riuscito"}
                            {status === "error" && "❌ Errore Login"}
                        </CardTitle>
                    </CardHeader>

                    <CardContent className="flex flex-col items-center gap-4">
                        {status === "loading" && (
                            <Loader2 className="h-12 w-12 animate-spin text-primary" />
                        )}

                        {status === "success" && (
                            <CheckCircle className="h-12 w-12 text-green-500" />
                        )}

                        {status === "error" && (
                            <XCircle className="h-12 w-12 text-red-500" />
                        )}

                        <p className="text-center text-muted-foreground">
                            {message}
                        </p>

                        {status === "loading" && (
                            <div className="text-xs text-muted-foreground">
                                Attendere prego...
                            </div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};
